<template>
    <div>
        <BaseInput
            v-slot="{field}"
            ref="baseInput"
            :model-value="modelValue"
            :label="label"
            :rules="rules"
            :name="name ? name : label"
            :required="required"
            validation-mode="aggressive">
            <input
                v-bind="field"
                class="d-none">
            <div
                :class="{'fixed-width': fixedWidth, 'fixed-width-sm': fixedWidthSm, 'center-label': centerLabel}"
                class="d-flex flex-wrap flex-gap">
                <slot />
                <template v-if="allowCustomAnswers">
                    <CardSelectItem
                        v-for="(option, index) in customOptions"
                        :key="'custom' + index"
                        :do-not-register-options="true"
                        :editable="true"
                        :value="option"
                        class="custom-item-input"
                        style="width: 180px">
                        <CardSelectItemCustomInput
                            :model-value="option"
                            :rules="customInputRules"
                            :name="(name ? name + ': ' : '') + $t('core.ownAnswer') + ' #' + index"
                            :label="$t('core.ownAnswer')"
                            @update:model-value="updateCustomAnswer($event, index)"
                            @click.stop="" />
                    </CardSelectItem>
                </template>
                <template v-else-if="Array.isArray(customOptions) && customOptions.length">
                    <CardSelectItem
                        v-for="(option, index) in customOptions"
                        :key="'custom' + index"
                        do-not-register-options
                        :value="option"
                        class="custom-item-input"
                        style="width: 180px">
                        <span class="text-through">{{ option }}</span>
                    </CardSelectItem>
                </template>
                <div v-if="showRadioResetCard">
                    <div
                        class="card pointer card-select-item d-flex flex-column center-label"
                        style="width:90px; min-height: 90px"
                        @click="resetAllOptions">
                        <div class="card-select-item-label my-0 flex-column m-1">
                            <font-awesome-icon
                                icon="times"
                                class="text-danger" />
                            <small class="font-weight-bold mt-1">{{ $t('core.removeOptions') }}</small>
                        </div>
                    </div>
                </div>
                <div v-if="allowAddNew">
                    <div
                        class="card pointer card-select-item d-flex flex-column center-label"
                        style="width:90px; min-height: 90px"
                        @click="$emit('addNew')">
                        <div class="card-select-item-label my-0 flex-column m-1">
                            <font-awesome-icon icon="plus" />
                            <small class="font-weight-bold mt-1">{{ $t('core.new') }}</small>
                        </div>
                    </div>
                </div>
                <div v-if="allowCustomAnswers">
                    <div
                        v-if="modelValue === null ||
                            modelValue === undefined ||
                            (type === 'radio' &&
                                (
                                    !Array.isArray(modelValue) && !customOptions.includes(modelValue) ||
                                    Array.isArray(modelValue) && !customOptions.includes(modelValue[0])
                                )
                            ) ||
                            (type === 'checkbox' && Array.isArray(modelValue) && !modelValue.includes(''))"
                        class="card pointer card-select-item d-flex flex-column"
                        @click="setOption('', false)">
                        <div class="card-select-item-label flex-column my-3">
                            <font-awesome-icon
                                class="mb-2"
                                icon="plus" />
                            <span class="font-weight-bold">{{ $t('core.addOwnAnswer') }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </BaseInput>
    </div>
</template>

<script>

export default {
    name: "CardSelect",
    provide() {
        return {
            selection: () => this.selection,
            type: this.type,
            setOption: this.setOption,
            registerOptionValue: this.registerOptionValue
        };
    },
    props: {
        type: {
            type: String,
            default: "checkbox",
            validate: (value) => ['checkbox', 'radio'].includes(value),
            description: "checkbox or radio"
        },
        modelValue: {
            required: true
        },
        required: {
            type: Boolean
        },
        name: {
            type: String
        },
        hideRadioResetCard: {
            type: Boolean
        },
        fixedWidth: {
            type: Boolean,
            default: true
        },
        fixedWidthSm: {
            type: Boolean
        },
        fixedWidthLg: {
            type: Boolean
        },
        label: {
            type: String,
            description: "Input label (text before input)"
        },
        allowCustomAnswers: {
            type: Boolean
        },
        forceArrayUsageForRadioInput: {
            type: Boolean
        },
        allowAddNew: {
            type: Boolean
        },
        centerLabel: {
            type: Boolean,
            default: true
        },
        customInputRules: {
            type: Object
        },
        rules: {
            type: [String, Object],
            default: null

        }
    },
    data() {
        return {
            registeredOptionValues: []
        }
    },
    computed: {
        selection() {
            return {
                selected: this.modelValue
            }
        },
        showRadioResetCard() {
            if (this.type === 'checkbox') return false
            if (this.hideRadioResetCard) return false
            if (this.required) return false
            if (!this.modelValue) return false
            if (Array.isArray(this.modelValue) && !this.modelValue.length) return false

            return true
        },
        customOptions() {
            if (this.modelValue === null || this.modelValue === undefined) {
                return []
            }

            let modelValues = null
            if (Array.isArray(this.modelValue)) {
                modelValues = this.modelValue
            } else {
                modelValues = [this.modelValue]
            }
            return modelValues.filter(modelValue => !this.registeredOptionValues.includes(modelValue))
        }
    },
    methods: {
        updateCustomAnswer(value, index) {
            if (Array.isArray(this.modelValue)) {
                const selectedValues = this.$cloneDeep(this.modelValue)

                for (let i = 0; i < selectedValues.length; i++) {
                    if (!this.registeredOptionValues.includes(selectedValues[i])) {
                        if (index === 0) {
                            selectedValues[i] = value
                        }
                        index--

                    }
                }
                this.$emit('update:modelValue', selectedValues)
            } else {
                this.$emit('update:modelValue', value)
            }
            this.$nextTick(() => {
                this.$refs.baseInput.$refs.validationProvider.validate()
            })
        },
        registerOptionValue(optionValue) {
            this.registeredOptionValues.push(optionValue)
        },
        resetAllOptions() {
            if (this.type === 'checkbox' || this.forceArrayUsageForRadioInput)
                this.$emit('update:modelValue', [])
            else
                this.$emit('update:modelValue', null)
            this.validate()
        },
        setOption(option, validateAfter = true) {
            if (this.type === 'checkbox') {
                this.setOptionWhenCheckbox(option)
            } else if (this.type === 'radio') {
                this.setOptionWhenRadio(option)
            } else {
                console.error("Unknown type:" + this.type)
            }
            if (validateAfter) this.validate()
        },
        setOptionWhenCheckbox(option) {
            let values = this.$cloneDeep(this.modelValue)
            values = Array.isArray(values) ? values : []

            const index = values.findIndex((value) => value === option)
            if (index !== -1) {
                values.splice(index, 1)
            } else {
                values.push(option)
            }

            this.$emit('update:modelValue', values)
        },
        setOptionWhenRadio(option) {
            //Allow the usage of arrays with radio inputs
            if (Array.isArray(this.modelValue) || this.forceArrayUsageForRadioInput) {
                this.$emit('update:modelValue', [option])
            } else {
                this.$emit('update:modelValue', option)
            }
        },
        validate() {
            this.$nextTick(() => {
                this.$refs.baseInput.$refs.validationProvider.validate()
            })
        }
    },
    watch: {
        value: {
            handler(value) {
                this.selection.selected = value
            },
            deep: true
        }

    }
}
</script>

<style lang="scss" scoped>
.flex-gap {
    gap: map-get($spacers, 3)
}

:deep(.card-select-item) {
    margin-bottom: 0;
    padding: map-get($spacers, 1) 0;
    height: 100%;
}

:deep(.card-select-item-label) {
    margin: map-get($spacers, 2) map-get($spacers, 3);
    flex-grow: 1;
}

:deep(.center-label .card-select-item-label) {
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: center;
}


.fixed-width :not(.custom-item-input) {
    :deep(.card-select-item) {
        min-width: 130px;
        max-width: min-content;
    }
}

.fixed-width-sm :not(.custom-item-input) {
    :deep(.card-select-item) {
        min-width: 80px;
        max-width: min-content;
    }
}

.fixed-width-lg :not(.custom-item-input) {
    :deep(.card-select-item) {
        min-width: 180px;
        max-width: min-content;
    }
}
</style>
